import { useOktaAuth } from '@okta/okta-react';
import { createContext, useContext, useEffect, useState } from 'react';
import { parseProblem } from './LoggedInContext';
import { UnitSelectionContext } from './UnitSelectionContext';

export const AdminContext = createContext({});

export const AdminContextProvider = ({ children, impersonatedId }) => {
    const {
        authState: {
            accessToken: { accessToken },
        },
    } = useOktaAuth();
    const { setSelectedUnit } = useContext(UnitSelectionContext);
    const [selectedAdminGroup, setSelectedAdminGroup] = useState();
    const [availableAdminGroups, setAvailableAdminGroups] = useState();
    const [error, setError] = useState();
    const [isJobFormDirty, setIsJobFormDirty] = useState(false);

    const loading = !selectedAdminGroup && !error;

    const hasCycleSettings = (biddingGroup) => {
        return (
            biddingGroup.daysInWithdrawalPeriod !== null &&
            biddingGroup.daysInActivePeriod !== null &&
            biddingGroup.timeToGoActive !== null &&
            biddingGroup.dayToGoActive !== null &&
            biddingGroup.timeToClose !== null
        );
    };

    useEffect(() => {
        let ignore = false;
        const fetchAdminGroupSettings = async () => {
            try {
                if (!ignore) {
                    const settingsResponse = await fetch(
                        `${process.env.REACT_APP_API_ENDPOINT}/settings/admin-groups`,
                        {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                actingAs: impersonatedId,
                            },
                        }
                    );
                    if (settingsResponse.ok) {
                        const adminGroups = await settingsResponse.json();
                        const mappedAdminGroups = adminGroups.map((ag) => {
                            const mappedBiddingGroups = ag.biddingGroups.map(
                                (bg) => {
                                    return {
                                        ...bg,
                                        workforceMovesAvailable:
                                            bg.workforceMovesAvailable ?? '',
                                        hasCycleSettings: hasCycleSettings(bg),
                                    };
                                }
                            );
                            return {
                                ...ag,
                                biddingGroups: mappedBiddingGroups,
                            };
                        });

                        setAvailableAdminGroups(mappedAdminGroups);
                        // temporarily show first admin group from available
                        setSelectedAdminGroup(mappedAdminGroups[0]);
                        setSelectedUnit?.(
                            mappedAdminGroups[0]?.adGroup ?? null
                        );
                        setError(false);
                    } else {
                        settingsResponse.problem = await parseProblem(
                            settingsResponse
                        );
                        setError(settingsResponse);
                    }
                }
            } catch (e) {
                setError(e);
            }
        };
        fetchAdminGroupSettings();

        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [accessToken, impersonatedId, setSelectedUnit]);

    // Load selected admin group from localStorage on mount or when availableAdminGroups change
    useEffect(() => {
        if (availableAdminGroups && availableAdminGroups.length > 0) {
            const savedAdGroup = localStorage.getItem('selectedAdminGroup');
            if (savedAdGroup) {
                const found = availableAdminGroups.find(
                    (ag) => ag.adGroup === savedAdGroup
                );
                if (found) {
                    setSelectedAdminGroup(found);
                    setSelectedUnit?.(found.adGroup);
                    return;
                }
            }
            setSelectedAdminGroup(availableAdminGroups[0]);
            setSelectedUnit?.(availableAdminGroups[0]?.adGroup ?? null);
        }
    }, [availableAdminGroups, setSelectedUnit]);

    useEffect(() => {
        return () => {
            setSelectedUnit?.(null);
        };
    }, [setSelectedUnit]);

    const changeSelectedAdminGroup = (adGroupName) => {
        const newAdminGroup = availableAdminGroups.find(
            (ag) => ag.adGroup === adGroupName
        );
        setSelectedAdminGroup(newAdminGroup);
        setSelectedUnit?.(adGroupName);
        localStorage.setItem('selectedAdminGroup', adGroupName);
    };

    const updateAdminGroup = (updatedGroup) => {
        const updatedAdminGroups = availableAdminGroups.map((ag) => {
            if (ag.adGroup === updatedGroup.adGroup) {
                return {
                    ...updatedGroup,
                };
            }
            return ag;
        });
        setAvailableAdminGroups(updatedAdminGroups);
        if (updatedGroup.adGroup === selectedAdminGroup.adGroup) {
            const updatedSelectedGroup = updatedAdminGroups.find(
                (ag) => ag.adGroup === selectedAdminGroup.adGroup
            );
            setSelectedAdminGroup(updatedSelectedGroup);
        }
    };

    const adminContextValue = {
        loading,
        selectedAdminGroup,
        availableAdminGroups,
        error,
        isJobFormDirty,
        setIsJobFormDirty,
        changeSelectedAdminGroup,
        updateAdminGroup,
    };

    return (
        <AdminContext.Provider value={adminContextValue}>
            {children}
        </AdminContext.Provider>
    );
};
